import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronRight, Image as ImageIcon, Video, Minus, Plus, Lock, Info } from 'lucide-react';
import AppleHeader from '../components/AppleHeader';
import { loadLastCrearAnuncioState, mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import CachedImg from '../components/CachedImg';
import { supabase } from '../lib/supabase';

export const SeleccionarFormatoAnuncio: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map((s) => s.trim()).filter(Boolean),
    [idsParam]
  );
  const modo = (params.get('modo') as 'rapido' | 'personalizado') || 'rapido';
  const objetivoParam = params.get('objetivo') as 'mostrar' | 'oferta' | 'lanzamiento' | 'evento' | null;
  const detalles_oferta = params.get('detalles_oferta') || '';
  const composicion = params.get('composicion') as 'modelo' | 'producto' | null;

  type TipoKey = 'imagen' | 'video';
  type FormatoKey = 'post' | 'story' | 'square';

  const savedStateRef = useRef(loadLastCrearAnuncioState());
  const FASHION_LABEL = 'Moda e Indumentaria';
  const isFashion = (savedStateRef.current?.rubro || FASHION_LABEL) === FASHION_LABEL;
  const [tipo, setTipo] = useState<TipoKey>(() => {
    const paramTipo = params.get('tipo') as TipoKey | null;
    if (paramTipo && ['imagen', 'video'].includes(paramTipo)) return paramTipo;
    const saved = savedStateRef.current?.tipo as TipoKey | undefined;
    return saved && ['imagen', 'video'].includes(saved) ? saved : 'imagen';
  });
  const [formato, setFormato] = useState<FormatoKey>(() => {
    const paramFormato = params.get('formato') as FormatoKey | null;
    if (paramFormato && ['post', 'story', 'square'].includes(paramFormato)) return paramFormato;
    const saved = savedStateRef.current?.formato as FormatoKey | undefined;
    return saved && ['post', 'story', 'square'].includes(saved) ? saved : 'post';
  });
  const [cantidad, setCantidad] = useState<number>(() => {
    const paramCantidad = params.get('cantidad_imagenes');
    const parsed = paramCantidad ? Number(paramCantidad) : NaN;
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    const saved = savedStateRef.current?.cantidadImagenes;
    if (typeof saved === 'number' && saved > 0) return saved;
    return modo === 'personalizado' ? 2 : 3;
  });
  const [modoPro, setModoPro] = useState<boolean>(() => {
    const paramModoPro = params.get('modo_pro');
    if (paramModoPro === '1') return true;
    return savedStateRef.current?.modoPro === true;
  });

  // Plan check for Modo Pro (only available for negocio and enterprise)
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<boolean>(true);
  const [showProPlanModal, setShowProPlanModal] = useState<boolean>(false);
  const isProPlanAllowed = userPlan === 'negocio' || userPlan === 'enterprise';

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoadingPlan(false);
          return;
        }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (!profile?.id_company) {
          setLoadingPlan(false);
          return;
        }
        const { data: company } = await supabase
          .from('company_profiles')
          .select('plan')
          .eq('id_company', profile.id_company)
          .single();
        if (company?.plan) {
          console.log('User plan from DB:', company.plan);
          setUserPlan(company.plan);
          // If user doesn't have pro plan access and modoPro is enabled, disable it
          if (company.plan !== 'negocio' && company.plan !== 'enterprise') {
            setModoPro(false);
          }
        } else {
          console.log('No plan found for company');
        }
      } catch (err) {
        console.error('Error fetching user plan:', err);
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchUserPlan();
  }, []);

  const formatOptions: Array<{ id: FormatoKey; name: string; image: string }> = [
    {
      id: 'post',
      name: 'Posteo (4:5)',
      image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Posteo.jpeg',
    },
    {
      id: 'story',
      name: 'Historias/Reels (9:16)',
      image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Historia.jpeg',
    },
    {
      id: 'square',
      name: 'Cuadrado (1:1)',
      image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Cuadrado.jpeg',
    },
  ];

  useEffect(() => {
    mergeLastCrearAnuncioState({
      modo,
      selectedVariantIds: ids,
      objetivo: objetivoParam || undefined,
      detallesOferta: detalles_oferta.trim() ? detalles_oferta.trim() : undefined,
      composicion: composicion || undefined,
      tipo,
      formato,
      cantidadImagenes: tipo === 'imagen' ? cantidad : null,
      modoPro,
    });
  }, [ids, modo, objetivoParam, detalles_oferta, composicion, tipo, formato, cantidad, modoPro]);

  const handleContinue = () => {
    if (!formato) return;
    const qp = new URLSearchParams();
    qp.set('ids', ids.join(','));
    qp.set('modo', modo);
    if (objetivoParam) qp.set('objetivo', objetivoParam);
    qp.set('tipo', tipo);
    qp.set('formato', formato);
    if (tipo === 'imagen') qp.set('cantidad_imagenes', String(cantidad));
    if (modoPro) qp.set('modo_pro', '1');
    if (detalles_oferta.trim()) qp.set('detalles_oferta', detalles_oferta.trim());
    if (composicion) qp.set('composicion', composicion);
    if (modo === 'rapido') {
      navigate(`/redes-sociales/crear-anuncio/seleccionar-estilo?${qp.toString()}`);
    } else {
      if (!isFashion) {
        if (!composicion) qp.set('composicion', 'modelo');
        navigate(`/redes-sociales/crear-anuncio/elegir-fondo?${qp.toString()}`);
      } else {
        const nextPersonalizado = composicion === 'modelo'
          ? '/redes-sociales/crear-anuncio/personalizar-modelo'
          : '/redes-sociales/crear-anuncio/elegir-fondo';
        navigate(`${nextPersonalizado}?${qp.toString()}`);
      }
    }
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      {/* Header + progress */}
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
        <AppleHeader title="Seleccioná el formato" />
        <div className="mt-4">
          <div className="w-full h-1 bg-gray-200 rounded-full">
            <div className="h-1 bg-blue-500 rounded-full" style={{ width: '75%' }} />
          </div>
        </div>

        {/* Tipo selector - temporarily hidden
        <div className="mt-6 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTipo('imagen')}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full border shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              tipo === 'imagen' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <ImageIcon className="w-4 h-4" />
            Imagen
          </button>
          <button
            type="button"
            disabled
            aria-disabled
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-gray-200 bg-white text-gray-500 text-sm shadow-sm opacity-60 cursor-not-allowed"
            title="Próximamente"
          >
            <Video className="w-4 h-4" />
            Video (Próximamente)
          </button>
        </div>
        */}

        {/* Cantidad (solo imagen) */}
        {tipo === 'imagen' && (
          <div className="mt-4 border border-gray-200 rounded-2xl p-3 sm:p-4 bg-white">
            <div className="flex items-center justify-between sm:justify-start gap-3">
              <label className="text-sm text-gray-700">Cantidad de imágenes a generar</label>
              <div className="inline-flex items-center rounded-full border border-gray-200 bg-white shadow-sm">
                <button
                  type="button"
                  aria-label="Disminuir"
                  onClick={() => setCantidad((c) => Math.max(1, c - 1))}
                  disabled={cantidad <= 1}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <div className="w-10 text-center text-sm text-gray-900 select-none">{cantidad}</div>
                <button
                  type="button"
                  aria-label="Aumentar"
                  onClick={() => setCantidad((c) => Math.min(8, c + 1))}
                  disabled={cantidad >= 8}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modo Pro */}
        {tipo === 'imagen' && (
          <div className="mt-4 border border-gray-200 rounded-2xl p-3 pb-4 sm:p-4 sm:pb-5 bg-white relative">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">Modo Pro</h3>
                  {!loadingPlan && !isProPlanAllowed && (
                    <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      <Lock className="w-3 h-3" />
                      Plan Pro / Ultra
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Hacé que tus imágenes se vean todavía más realistas y tengan una mayor calidad.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={modoPro}
                onClick={() => {
                  if (isProPlanAllowed) {
                    setModoPro(!modoPro);
                  } else if (!loadingPlan) {
                    setShowProPlanModal(true);
                  }
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer ${
                  modoPro && isProPlanAllowed ? 'bg-blue-500' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    modoPro && isProPlanAllowed ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {isProPlanAllowed && (
              <p className="text-xs text-gray-900 mt-3 font-medium flex items-center gap-1.5">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                Una imagen en Modo Pro consume 4 imágenes normales.
              </p>
            )}

            {/* Pro Plan Modal */}
            {showProPlanModal && (
              <>
                <div 
                  className="fixed inset-0 z-40"
                  onClick={() => setShowProPlanModal(false)}
                />
                <div className="absolute right-0 top-full mt-2 z-50 w-72 bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Lock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900">Función exclusiva</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        El Modo Pro está disponible solo para los planes Pro y Ultra.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowProPlanModal(false);
                      navigate('/subscription');
                    }}
                    className="mt-4 w-full bg-blue-500 hover:bg-blue-600 text-white text-sm font-normal py-2 px-4 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Ver planes
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Formatos */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {formatOptions.map((opt) => {
            const selected = formato === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFormato(opt.id)}
                className={`w-full text-left border ${selected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'} rounded-2xl p-3 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
              >
                <div className="relative overflow-hidden rounded-xl">
                  <CachedImg src={opt.image} alt={opt.name} className="w-full h-40 object-cover" />
                  {selected && (
                    <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-900 mt-2 font-semibold">{opt.name}</p>
              </button>
            );
          })}
        </div>

        {/* Footer actions (desktop) */}
        <div className="mt-6 hidden sm:flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>

          <button
            type="button"
            disabled={!formato}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${!formato ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            onClick={handleContinue}
          >
            Continuar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile spacer for fixed bottom bar */}
      <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />

      {/* Mobile bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Volver
          </button>
          <button
            type="button"
            disabled={!formato}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${!formato ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            onClick={handleContinue}
          >
            Continuar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeleccionarFormatoAnuncio;
